/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { useState, useMemo } from 'react';
import { useStore } from '../../modules/store/cobrandStore';
import { StyledComparisonTray } from './CobrandComparisonTray.styles';
import { CompareCard } from './CompareCard';
import { isMobile } from '../../modules/utils';
import { Drawer } from '@marriott/mi-groups-components';
import { useCheckBreakpoint, Button, Heading, tags, size, headingType } from '@marriott/mi-ui-library';
import { ComparisonTraySlotDesktop, ComparisonTraySlotMobile } from '../../modules/constants';
import { CtaComponent } from '../../molecules/CtaComponent/CtaComponent';
import { CtaComponentTypes } from '../../molecules/CtaComponent/CtaComponent.types';
import clsx from 'clsx';
import { CobrandComparisonTrayProps } from './CobrandComparisonTray.types';

export const CobrandComparisonTray = (props: CobrandComparisonTrayProps) => {
  const isViewportL = useCheckBreakpoint('viewportL');
  const isMobileDevice = isMobile();

  const ENABLE_COMPARISON_TRAY = String(props?.isComparisonTrayEnabled) === 'true';
  const { cobrandData, setCobrandData } = useStore(store => store);
  const [visible, setVisible] = useState(true);

  // Memoize slots for performance and clarity
  const slots = useMemo(
    () => (isViewportL && !isMobileDevice ? ComparisonTraySlotDesktop : ComparisonTraySlotMobile),
    [isViewportL, isMobileDevice]
  );

  // Extract CTA props from model
  const ctaProps: CtaComponentTypes | undefined = useMemo(() => {
    const cqItems = props?.['cqItems'] as Record<string, any>;
    const pageItems = props?.isFromWrapper
      ? cqItems?.['variable-column-0']?.[':items'] ?? {}
      : props?.model?.cqItems?.['page-0']?.[':items'] ?? {};

    for (const itemKey in pageItems) {
      if (itemKey.includes('cta_component')) {
        return pageItems[itemKey] as CtaComponentTypes;
      }
    }
    return undefined;
  }, [props]);

  const handleClearAll = () => {
    setCobrandData({ ...cobrandData, selectedCards: [] });
  };

  const handleDrawerChange = () => {
    setVisible(v => !v);
  };

  // Hide component if no CTA props
  if (!ctaProps) return null;

  // Hide if no selected cards or feature disabled
  if (!cobrandData?.selectedCards?.length || !ENABLE_COMPARISON_TRAY) {
    return null;
  }

  const cardsSelectedText = `Cards Selected: ${cobrandData.selectedCards.length} of ${slots.length}`;

  return (
    <Drawer
      variant="drawer"
      placement="bottom"
      initialState={visible}
      title={
        <StyledComparisonTray isCollapsed>
          <div className="container py-2 d-flex flex-row justify-content-between align-items-center">
            <div>
              <Heading
                titleText={cardsSelectedText}
                fontSize={size.large}
                element={tags.h5}
                variation={headingType.subtitle}
              />
            </div>

            <div className="d-flex flex-row align-items-center">
              <Button
                isLink={true}
                buttonCopy="Clear all"
                callback={handleClearAll}
                className={clsx('clear-btn ', isViewportL && !isMobileDevice ? 'd-block mr-2' : 'd-none')}
              />
              <div className="ml-1">
                <CtaComponent {...ctaProps} comparisonTrayFlag={true} />
              </div>
            </div>
          </div>
        </StyledComparisonTray>
      }
      onChange={handleDrawerChange}
    >
      <StyledComparisonTray data-testid="cobrandcomparisontray" data-component-name="o-cobrand-cobrandcomparisontray">
        <div className="container pt-4">
          <Heading
            titleText={cardsSelectedText}
            fontSize={size.large}
            element={tags.h5}
            variation={headingType.subtitle}
          />
          <div className="compare-cards d-flex flex-column flex-md-row mt-3">
            <div className="d-flex flex-column flex-md-row">
              {slots.map(index => {
                const cardItem = cobrandData.selectedCards[index];
                return (
                  <CompareCard
                    key={index}
                    index={index}
                    item={cardItem}
                    defaultContent={!cardItem}
                    styleClass="mr-md-2 mb-3 mb-md-0"
                    data-testid={`compare-card-${index}`}
                  />
                );
              })}
            </div>
            <div className="d-flex justify-content-end align-items-center">
              <Button
                isLink
                buttonCopy="Clear all"
                callback={handleClearAll}
                className={clsx('clear-btn', isViewportL && !isMobileDevice ? 'd-block mr-4' : 'd-none')}
              />
              <CtaComponent {...ctaProps} comparisonTrayFlag={true} />
            </div>
          </div>
        </div>
      </StyledComparisonTray>
    </Drawer>
  );
};
